// The collaboration layer's state for the whole studio: who I am, the workspace's people, the presence
// room of the open project (roster in a small store, so 15 cursor frames a second re-render only what
// draws cursors), following someone, comment mode and "Show resolved", the comment surface that ] and [
// walk, and the inbox's unread count. Keys: C comment mode, Esc leaves it (or stops following), ⇧C
// shows resolved pins, ] and [ go to the next and previous comment.
import {
  emptyRoster,
  followTarget,
  followView,
  type PresencePeer,
  type PresenceRoster,
  type PresenceViewing,
  type PresenceWhere,
  type ProjectPresence,
  samePlace,
} from "@filmsquare/realtime/presence";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type Api, defaultBaseUrl } from "../api.ts";
import type { SocketLike } from "../canvas/connection.ts";
import { formatRoute, type Route } from "../route.ts";
import type { Role } from "../types.ts";
import { isTyping } from "../ui/app.ts";
import { pollDelay } from "./inbox.ts";
import { InboxClient } from "./inboxClient.ts";
import { type PresenceStatus, ProjectPresenceClient } from "./presenceClient.ts";
import { type Member, memberName, type ReviewApi, reviewApi } from "./reviewApi.ts";
import { projectOfRoute, routeOfWhere, type WhereResolver, whereOfRoute } from "./where.ts";

export interface RosterState {
  status: PresenceStatus;
  roster: PresenceRoster;
}

/** A tiny external store for the roster: subscribe + get, like useSyncExternalStore wants. */
export interface RosterStore {
  get: () => RosterState;
  subscribe: (fn: () => void) => () => void;
}

/** A comment surface on screen (stage, canvas, inspector list): what ] and [ walk through. */
export interface CommentSurface {
  step: (dir: 1 | -1) => void;
}

export interface FocusRequest {
  threadId: string;
  assetVersionId?: string | null;
  takeId?: string | null;
}

export interface CollabCtx {
  rapi: ReviewApi;
  role: Role | undefined;
  projectId: string | null;
  meId: string | null;
  members: Member[];
  nameOf: (userId: string | null | undefined) => string;
  store: RosterStore;
  setPresence: (patch: ProjectPresence) => void;
  /** Label of where I am (the page knows the names). */
  setWhereLabel: (label: string | undefined) => void;
  setResolver: (r: WhereResolver) => void;
  following: string | null;
  follow: (peerId: string | null) => void;
  /** The take/version my leader has on stage (the stage follows it). */
  leaderViewing: PresenceViewing | null;
  /** I moved on my own (picked a take, scrolled the board): stop following. */
  userMoved: () => void;
  commentMode: boolean;
  setCommentMode: (on: boolean) => void;
  showResolved: boolean;
  setShowResolved: (on: boolean) => void;
  registerSurface: (s: CommentSurface) => () => void;
  focus: FocusRequest | null;
  requestFocus: (f: FocusRequest | null) => void;
  unread: number;
  /** The server did not answer the last inbox poll: the top bar then shows no stale numbers. */
  offline: boolean;
  refreshUnread: () => void;
  inboxOpen: boolean;
  setInboxOpen: (open: boolean) => void;
  /** Bumped when a thread changed here, so other lists of the same item refetch at once. */
  threadsTick: number;
  bumpThreads: () => void;
}

const Ctx = createContext<CollabCtx | null>(null);

/** The collaboration context, or null outside the provider (pages then simply show no collaboration). */
export const useCollab = (): CollabCtx | null => useContext(Ctx);

const newClientId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `c${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

const timers = {
  now: () => Date.now(),
  setTimeout: (fn: () => void, ms: number) => setTimeout(fn, ms),
  clearTimeout: (h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>),
  setInterval: (fn: () => void, ms: number) => setInterval(fn, ms),
  clearInterval: (h: unknown) => clearInterval(h as ReturnType<typeof setInterval>),
};

export function CollabProvider({
  api,
  ws,
  role,
  route,
  go,
  children,
}: {
  api: Api;
  ws: string | undefined;
  role: Role | undefined;
  route: Route;
  go: (r: Route) => void;
  children: ReactNode;
}) {
  const rapi = useMemo(() => reviewApi(api, ws ?? ""), [api, ws]);
  const projectId = ws ? projectOfRoute(route) : null;
  const [meId, setMeId] = useState<string | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [commentMode, setCommentMode] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [following, setFollowing] = useState<string | null>(null);
  const [leader, setLeader] = useState<PresencePeer | null>(null);
  const [focus, requestFocus] = useState<FocusRequest | null>(null);
  const [unread, setUnread] = useState(0);
  const [offline, setOffline] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [threadsTick, setThreadsTick] = useState(0);

  // ── who I am and who is in the workspace ──
  useEffect(() => {
    if (!ws) return;
    let live = true;
    rapi.me().then(
      (r) => live && setMeId(r.user?.id ?? null),
      () => live && setMeId(null),
    );
    rapi.members().then(
      (r) => live && setMembers(r.items),
      () => live && setMembers([]),
    );
    return () => {
      live = false;
    };
  }, [rapi, ws]);
  const byId = useMemo(() => new Map(members.map((m) => [m.user_id, m])), [members]);
  const nameOf = useCallback(
    (u: string | null | undefined) => (u ? memberName(byId.get(u)) : "Someone"),
    [byId],
  );

  // ── the roster store ──
  const rosterRef = useRef<RosterState>({ status: "idle", roster: emptyRoster() });
  const listeners = useRef(new Set<() => void>());
  const store = useMemo<RosterStore>(
    () => ({
      get: () => rosterRef.current,
      subscribe: (fn) => {
        listeners.current.add(fn);
        return () => listeners.current.delete(fn);
      },
    }),
    [],
  );

  // ── the presence connection of the open project ──
  const clientRef = useRef<ProjectPresenceClient | null>(null);
  const labelRef = useRef<string | undefined>(undefined);
  const resolverRef = useRef<WhereResolver>({});
  const clientId = useMemo(newClientId, []);
  useEffect(() => {
    if (!projectId || !ws) return;
    const client = new ProjectPresenceClient({
      getSession: () => rapi.presenceSession(projectId),
      createSocket: (url) => new WebSocket(url) as unknown as SocketLike,
      apiBase: defaultBaseUrl(),
      pageOrigin: window.location.origin,
      clientId,
      timers,
      onChange: (s) => {
        rosterRef.current = s;
        for (const fn of listeners.current) fn();
      },
    });
    clientRef.current = client;
    client.start();
    return () => {
      client.stop();
      clientRef.current = null;
      rosterRef.current = { status: "idle", roster: emptyRoster() };
      for (const fn of listeners.current) fn();
    };
  }, [projectId, ws, rapi, clientId]);

  const setPresence = useCallback((patch: ProjectPresence) => clientRef.current?.update(patch), []);

  // where I am: from the route, labelled by the page
  const where = useMemo(() => whereOfRoute(route), [route]);
  const whereKey = where ? `${where.kind}:${where.id}` : "";
  // biome-ignore lint/correctness/useExhaustiveDependencies: whereKey stands for where
  useEffect(() => {
    if (!clientRef.current) return;
    const w: PresenceWhere | undefined = where
      ? { ...where, ...(labelRef.current ? { label: labelRef.current } : {}) }
      : undefined;
    // Stages own viewing/cursor lifecycle, including stages outside shot routes (delivery review).
    clientRef.current.update({ where: w });
  }, [whereKey, projectId]);
  const setWhereLabel = useCallback(
    (label: string | undefined) => {
      labelRef.current = label;
      if (where && clientRef.current)
        clientRef.current.update({ where: { ...where, ...(label ? { label } : {}) } });
    },
    [where],
  );
  const setResolver = useCallback((r: WhereResolver) => {
    resolverRef.current = r;
  }, []);

  // ── following ──
  const expectHash = useRef<string | null>(null);
  const routeHash = formatRoute(route);
  const routeHashRef = useRef(routeHash);
  routeHashRef.current = routeHash;
  const follow = useCallback((peerId: string | null) => {
    setFollowing(peerId);
    expectHash.current = peerId ? routeHashRef.current : null;
    clientRef.current?.update({ following: peerId });
  }, []);
  const userMoved = useCallback(() => {
    if (following) follow(null);
  }, [following, follow]);

  // Track the leader in the roster (the chain is walked by followTarget).
  useEffect(() => {
    if (!following) {
      setLeader(null);
      return;
    }
    const read = () => {
      const { roster } = store.get();
      const self = roster.self;
      const l = self
        ? followTarget(Object.values(roster.peers), { id: self.id, presence: { following } })
        : null;
      setLeader((prev) => (prev?.id === l?.id && prev?.presence === l?.presence ? prev : l));
      if (!l && store.get().status === "live" && self) follow(null); // the leader left
    };
    read();
    return store.subscribe(read);
  }, [following, store, follow]);

  // Go where the leader goes.
  const leaderWhere = leader ? followView(leader)?.where : undefined;
  const leaderPlace = leaderWhere ? `${leaderWhere.kind}:${leaderWhere.id}` : "";
  // biome-ignore lint/correctness/useExhaustiveDependencies: leaderPlace stands for leaderWhere
  useEffect(() => {
    if (!leaderWhere || !projectId) return;
    if (samePlace(whereOfRoute(route), leaderWhere)) return;
    const to = routeOfWhere(projectId, leaderWhere, resolverRef.current);
    if (!to) return;
    expectHash.current = formatRoute(to);
    go(to);
  }, [leaderPlace, projectId]);

  // Navigating on my own stops following.
  // biome-ignore lint/correctness/useExhaustiveDependencies: run on navigation only
  useEffect(() => {
    if (!following) return;
    if (expectHash.current === routeHash) return;
    if (leaderWhere && samePlace(whereOfRoute(route), leaderWhere)) return;
    follow(null);
  }, [routeHash]);

  // ── comment surfaces (] and [) ──
  const surfaces = useRef<CommentSurface[]>([]);
  const registerSurface = useCallback((s: CommentSurface) => {
    surfaces.current = [...surfaces.current, s];
    return () => {
      surfaces.current = surfaces.current.filter((x) => x !== s);
    };
  }, []);

  // ── inbox: unread count, pushed through the inbox socket with a poll behind it (see inbox.ts) ──
  const refreshUnread = useCallback(() => {
    if (!ws) return;
    rapi.unreadCount().then(
      (r) => {
        setUnread(r.unread);
        setOffline(false);
      },
      () => setOffline(true),
    );
  }, [rapi, ws]);
  // Read by the poll's next schedule: a socket going live or down changes the pace from the next tick on.
  const pushed = useRef(false);
  useEffect(() => {
    if (!ws) return;
    const client = new InboxClient({
      getSession: () => rapi.inboxSession(),
      createSocket: (url) => new WebSocket(url) as unknown as SocketLike,
      apiBase: defaultBaseUrl(),
      pageOrigin: window.location.origin,
      clientId: `${clientId}-inbox`,
      timers,
      onSignal: refreshUnread,
      onStatus: (s) => {
        pushed.current = s === "live";
      },
    });
    client.start();
    return () => {
      client.stop();
      pushed.current = false;
    };
  }, [ws, rapi, clientId, refreshUnread]);
  useEffect(() => {
    if (!ws) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = null;
      const ms = pollDelay({
        visible: document.visibilityState === "visible",
        focused: document.hasFocus(),
        pushed: pushed.current,
      });
      if (ms !== null)
        timer = setTimeout(() => {
          refreshUnread();
          schedule();
        }, ms);
    };
    const wake = () => {
      refreshUnread();
      schedule();
    };
    wake();
    window.addEventListener("focus", wake);
    window.addEventListener("blur", schedule);
    document.addEventListener("visibilitychange", wake);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("focus", wake);
      window.removeEventListener("blur", schedule);
      document.removeEventListener("visibilitychange", wake);
    };
  }, [ws, refreshUnread]);

  // ── keys ──
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (!projectId || e.defaultPrevented || isTyping(e) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector("[role=dialog][aria-modal=true]")) return;
      if (e.code === "KeyC" && !e.repeat) {
        e.preventDefault();
        if (e.shiftKey) setShowResolved((s) => !s);
        else setCommentMode((m) => !m);
      } else if (e.key === "Escape") {
        if (commentMode) {
          e.preventDefault();
          setCommentMode(false);
        } else if (following) {
          e.preventDefault();
          follow(null);
        }
      } else if (e.key === "]" || e.key === "[") {
        const s = surfaces.current[surfaces.current.length - 1];
        if (!s) return;
        e.preventDefault();
        s.step(e.key === "]" ? 1 : -1);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [projectId, commentMode, following, follow]);

  // Comment mode ends when the page changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: on navigation only
  useEffect(() => {
    if (!following) setCommentMode(false);
  }, [routeHash]);

  const bumpThreads = useCallback(() => setThreadsTick((n) => n + 1), []);

  const value = useMemo<CollabCtx>(
    () => ({
      rapi,
      role,
      projectId,
      meId,
      members,
      nameOf,
      store,
      setPresence,
      setWhereLabel,
      setResolver,
      following,
      follow,
      leaderViewing: leader?.presence?.viewing ?? null,
      userMoved,
      commentMode,
      setCommentMode,
      showResolved,
      setShowResolved,
      registerSurface,
      focus,
      requestFocus,
      unread,
      offline,
      refreshUnread,
      inboxOpen,
      setInboxOpen,
      threadsTick,
      bumpThreads,
    }),
    [
      rapi,
      role,
      projectId,
      meId,
      members,
      nameOf,
      store,
      setPresence,
      setWhereLabel,
      setResolver,
      following,
      follow,
      leader,
      userMoved,
      commentMode,
      showResolved,
      registerSurface,
      focus,
      unread,
      offline,
      refreshUnread,
      inboxOpen,
      threadsTick,
      bumpThreads,
    ],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
